/**
 * Renders a Yes/No confirmation dialog with two styled buttons.
 *
 * Supports:
 *   - Custom labels for each button
 *   - Detail text below the question
 *   - Destructive mode (red yes button for dangerous actions)
 *   - Disabled state after the user responds
 *
 * Uses Deckard's theme palette tokens for proper light/dark mode support.
 */
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { Box, useTheme } from '@mui/material';
import type { RichBlockProps } from './richBlockTypes';

export const YesNoBlock = ({ block, onRespond }: RichBlockProps) => {
  // Only block interaction when explicitly answered — expired blocks remain
  // clickable so the user can re-send their selection as a new message.
  const isAnswered = block.status === 'answered';
  const answeredValue = (block.response as { answer?: string } | undefined)?.answer;
  const {
    question,
    detail,
    yesLabel = 'Yes',
    noLabel = 'No',
    destructive = false,
  } = block.payload as {
    question: string;
    detail?: string;
    yesLabel?: string;
    noLabel?: string;
    destructive?: boolean;
  };

  const handleClick = (answer: 'yes' | 'no', label: string) => {
    if (isAnswered) return;
    onRespond?.(block.blockId, { answer, label });
  };

  return (
    <Box mt={1} mb={2} data-testid="rich-block-yes-no">
      <Typography variant="subtitle1" color="agentChat.message.agent.foreground.default">
        {question}
      </Typography>
      {detail && (
        <Typography variant="caption2" color="agentChat.message.agent.foreground.light">
          {detail}
        </Typography>
      )}
      <Group gap={2} mt={2}>
        <YesNoButton
          answered={isAnswered}
          variant={destructive ? 'destructive' : 'primary'}
          selected={answeredValue === 'yes'}
          label={yesLabel}
          onClick={() => handleClick('yes', yesLabel)}
          dataTestId="yes-no-yes-btn"
        />
        <YesNoButton
          answered={isAnswered}
          variant="secondary"
          selected={answeredValue === 'no'}
          label={noLabel}
          onClick={() => handleClick('no', noLabel)}
          dataTestId="yes-no-no-btn"
        />
      </Group>
    </Box>
  );
};

const YesNoButton = ({
  answered = false,
  label,
  selected = false,
  variant = 'primary',
  dataTestId,
  onClick,
}: {
  variant: 'primary' | 'secondary' | 'destructive';
  onClick: () => void;
  label: string;
  answered?: boolean;
  selected?: boolean;
  dataTestId: string;
}) => {
  const { palette, typography } = useTheme();
  return (
    <Box
      onClick={onClick}
      role="button"
      sx={{
        flex: 1,
        p: 1.25,
        borderRadius: 1,
        cursor: answered ? 'default' : 'pointer',
        textAlign: 'center',
        ...typography.body1,
        border: '1px solid',
        transition: 'all 0.15s',
        opacity: answered && !selected ? 0.4 : 1,
        ...(selected
          ? {
              backgroundColor: palette.agentChat.message.button[variant].hover.background,
              borderColor: palette.agentChat.message.button[variant].hover.border,
              color: palette.agentChat.message.button[variant].hover.foreground,
            }
          : {
              backgroundColor: palette.agentChat.message.button[variant].background,
              borderColor: palette.agentChat.message.button[variant].border,
              color: palette.agentChat.message.button[variant].foreground,
              '&:hover': answered
                ? {}
                : {
                    backgroundColor: palette.agentChat.message.button[variant].hover.background,
                    borderColor: palette.agentChat.message.button[variant].hover.border,
                    color: palette.agentChat.message.button[variant].hover.foreground,
                  },
            }),
      }}
      data-testid={dataTestId}
    >
      {label}
    </Box>
  );
};
